import { roundMoney } from "../../../utils/formatPrice";
import {
  formatPromotionBadge,
  getManufacturerPackQuantity,
  isQuantityBasedPromoType,
  promoShowsCaptionBadge,
} from "../sales/promotionUtils";

/** Standard Israeli VAT rate (selling prices include VAT). */
export const DEFAULT_VAT_RATE = 0.18;

/** Nominal container deposit (₪0.30). For cost/margin, VAT is deducted from it. */
export const DEFAULT_DEPOSIT_AMOUNT = 0.3;

const getProductPackageQuantity = (product) => {
  const logistics = product?.logisticsDetails ?? product?.logistics_details;
  const raw =
    logistics?.packageQuantity ??
    logistics?.package_quantity ??
    product?.packageQuantity ??
    product?.package_quantity;
  const qty = Number(raw);
  return Number.isFinite(qty) && qty > 0 ? qty : 0;
};

export const getDepositAmount = (
  product,
  depositAmount = DEFAULT_DEPOSIT_AMOUNT,
) => {
  if (!product?.depositItem) return 0;
  const packageQty = getProductPackageQuantity(product);
  return packageQty > 0
    ? roundMoney(depositAmount * packageQty)
    : depositAmount;
};

/** Deposit contribution after VAT deduction (unless the product is VAT-exempt). */
export const getNetDepositAmount = (
  depositAmount = 0,
  { vatExempt = false, vatRate = DEFAULT_VAT_RATE } = {},
) => {
  const deposit = Number(depositAmount) || 0;
  if (!deposit) return 0;
  if (vatExempt) return roundMoney(deposit);
  return roundMoney(deposit / (1 + vatRate));
};

/**
 * Purchase price for one manufacturer pack.
 * Purchase price is already for the sold SKU (including packaged products),
 * so pack cost is purchase price × manufacturer pack quantity.
 */
export const getManufacturerPackPurchasePrice = (purchasePrice, product) => {
  const packQty = getManufacturerPackQuantity(product);
  if (!(packQty > 0)) return null;
  const base = parseFloat(purchasePrice);
  if (!Number.isFinite(base)) return null;
  return roundMoney(base * packQty);
};

/** Base purchase price from a manufacturer-pack total. */
export const getPurchasePriceFromManufacturerPack = (packPrice, product) => {
  const packQty = getManufacturerPackQuantity(product);
  if (!(packQty > 0)) return null;
  const pack = parseFloat(packPrice);
  if (!Number.isFinite(pack)) return null;
  return roundMoney(pack / packQty);
};

/** Purchase price with net deposit added; null when there is no deposit. */
export const getPriceIncludingDeposit = (
  price,
  depositAmount = 0,
  { vatExempt = false, vatRate = DEFAULT_VAT_RATE } = {},
) => {
  const base = parseFloat(price);
  if (!Number.isFinite(base) || !depositAmount) return null;
  const netDeposit = getNetDepositAmount(depositAmount, { vatExempt, vatRate });
  return roundMoney(base + netDeposit);
};

/** Base price after removing net deposit; null when there is no deposit. */
export const getPriceExcludingDeposit = (
  price,
  depositAmount = 0,
  { vatExempt = false, vatRate = DEFAULT_VAT_RATE } = {},
) => {
  const total = parseFloat(price);
  if (!Number.isFinite(total) || !depositAmount) return null;
  const netDeposit = getNetDepositAmount(depositAmount, { vatExempt, vatRate });
  return roundMoney(Math.max(total - netDeposit, 0));
};

export const getProfitMarginVatHint = (
  vatExempt,
  vatRate = DEFAULT_VAT_RATE,
) => {
  if (vatExempt) return "ללא ניכוי מע״מ (מוצר פטור)";
  const percent = Math.round(vatRate * 100);
  return `בניכוי מע״מ (${percent}%)`;
};

/** True when both inputs parse to the same 2-decimal money value. */
export const isSamePriceValue = (a, b) => {
  const left = parseFloat(a);
  const right = parseFloat(b);
  if (!Number.isFinite(left) || !Number.isFinite(right)) return false;
  return roundMoney(left) === roundMoney(right);
};

export const isValidDiscountPercent = (value) => {
  const n = parseFloat(value);
  return Number.isFinite(n) && n >= 0 && n <= 100;
};

/** Base purchase price after a 0–100 discount percent. Independent of promotions. */
export const priceAfterDiscount = (price, discountPercent) => {
  const base = parseFloat(price);
  if (!Number.isFinite(base)) return null;
  const percent = parseFloat(discountPercent);
  const clamped = Number.isFinite(percent)
    ? Math.max(0, Math.min(100, percent))
    : 0;
  return roundMoney(Math.max(0, base * (1 - clamped / 100)));
};

/** Inverse of `priceAfterDiscount`. Clamped to 0–100. */
export const discountPercentFromPrices = (price, finalPrice) => {
  const base = parseFloat(price);
  const final = parseFloat(finalPrice);
  if (!Number.isFinite(base) || base <= 0 || !Number.isFinite(final)) return 0;
  return roundMoney(Math.max(0, Math.min(100, (1 - final / base) * 100)));
};

export const getNetSellingPrice = (
  sellingPrice,
  { vatExempt = false, vatRate = DEFAULT_VAT_RATE } = {},
) => {
  const selling = parseFloat(sellingPrice);
  if (!Number.isFinite(selling)) return null;
  if (vatExempt) return roundMoney(selling);
  return roundMoney(selling / (1 + vatRate));
};

/**
 * Margin revenue basis. Cost already includes net deposit, so selling is
 * converted ex-VAT on its own (no extra deposit on the revenue side).
 */
export const getNetRevenueForMargin = (
  sellingPrice,
  { vatExempt = false, vatRate = DEFAULT_VAT_RATE } = {},
) => getNetSellingPrice(sellingPrice, { vatExempt, vatRate });

export const getVatAmount = (
  sellingPrice,
  { vatExempt = false, vatRate = DEFAULT_VAT_RATE } = {},
) => {
  const selling = parseFloat(sellingPrice);
  if (!Number.isFinite(selling) || vatExempt) return 0;
  const net = getNetSellingPrice(selling, { vatExempt: false, vatRate });
  return net == null ? 0 : roundMoney(selling - net);
};

export const getActivePromo = (product) =>
  product?.pricing?.promotions?.[0] ?? null;

export const getEffectiveSellingPrice = (listPrice, promo) => {
  const list = parseFloat(listPrice);
  if (!Number.isFinite(list)) return null;
  if (!promo) return list;

  switch (promo.promoType) {
    case "FLAT":
      return parseFloat(promo.discountValue);
    case "PERCENT":
      return roundMoney(list * (1 - promo.discountValue / 100));
    default:
      return list;
  }
};

export const listPriceFromEffective = (effectivePrice, promo) => {
  const target = roundMoney(effectivePrice);
  if (!promo) return target;

  switch (promo.promoType) {
    case "PERCENT":
      return roundMoney(target / (1 - promo.discountValue / 100));
    case "FLAT":
    default:
      return target;
  }
};

export const calcProfitMargin = (
  costPrice,
  sellingPrice,
  { vatExempt = false, vatRate = DEFAULT_VAT_RATE } = {},
) => {
  const cost = parseFloat(costPrice);
  const netSelling = getNetRevenueForMargin(sellingPrice, {
    vatExempt,
    vatRate,
  });
  if (!Number.isFinite(cost) || netSelling == null || netSelling <= 0) {
    return "";
  }
  return roundMoney(((netSelling - cost) / netSelling) * 100).toFixed(2);
};

export const effectivePriceFromMargin = (
  costPrice,
  marginPercent,
  { vatExempt = false, vatRate = DEFAULT_VAT_RATE } = {},
) => {
  const cost = parseFloat(costPrice);
  const margin = parseFloat(marginPercent);
  if (
    !Number.isFinite(cost) ||
    !Number.isFinite(margin) ||
    cost <= 0 ||
    margin >= 100
  ) {
    return null;
  }
  const netSelling = roundMoney(cost / (1 - margin / 100));
  if (vatExempt) return netSelling;
  return roundMoney(netSelling * (1 + vatRate));
};

export const costPriceFromMargin = (
  sellingPrice,
  marginPercent,
  { vatExempt = false, vatRate = DEFAULT_VAT_RATE } = {},
) => {
  const margin = parseFloat(marginPercent);
  const netSelling = getNetRevenueForMargin(sellingPrice, {
    vatExempt,
    vatRate,
  });
  if (!Number.isFinite(margin) || netSelling == null || margin >= 100) {
    return null;
  }
  return roundMoney(netSelling * (1 - margin / 100));
};

export const getPromoSalePrice = (listPrice, promo) => {
  if (!promo || listPrice == null) return null;
  if (!["FLAT", "PERCENT"].includes(promo.promoType)) return null;
  return getEffectiveSellingPrice(listPrice, promo);
};

/** Effective unit sale price for any promo type; null when it cannot be derived. */
export const getCalculatedPromoSalePrice = (listPrice, promo) => {
  if (!promo) return null;

  switch (promo.promoType) {
    case "FLAT": {
      const flat = parseFloat(promo.discountValue);
      return Number.isFinite(flat) ? roundMoney(flat) : null;
    }
    case "PERCENT":
      return getEffectiveSellingPrice(listPrice, promo);
    case "QUANTITY_PRICE":
    case "PACKAGE_PRICE": {
      const qty = parseFloat(promo.minQuantity);
      const total = parseFloat(promo.discountValue);
      if (!Number.isFinite(qty) || qty <= 0 || !Number.isFinite(total)) {
        return null;
      }
      return roundMoney(total / qty);
    }
    default:
      return null;
  }
};

/** Only the ACTIVE promotion drives the sale-price / margin fields. */
export const pickDisplayPromotion = (promotions = []) => {
  if (!Array.isArray(promotions) || promotions.length === 0) return null;
  return promotions.find((promo) => promo?.status === "ACTIVE") || null;
};

export const isFlatPromoType = (promo) => promo?.promoType === "FLAT";

export const promoShowsStrikePrice = (promo) =>
  promo != null && !isQuantityBasedPromoType(promo.promoType);

/**
 * Compare-hero caption — same wording as product-card for qty/threshold tags.
 * Package (compare-only): regular hero price + tag with package unit sale (no icons).
 */
export const getPromoCompareBadgeParts = (promo, listPrice) => {
  if (!promoShowsCaptionBadge(promo)) return null;
  const badge = formatPromotionBadge(promo, { listPrice });
  if (!badge) return null;
  return { label: badge.text, note: null, icon: null };
};

export const getPromoCompareBadgeLabel = (promo, listPrice) => {
  const parts = getPromoCompareBadgeParts(promo, listPrice);
  if (!parts) return null;
  return parts.note ? `${parts.label} ${parts.note}` : parts.label;
};
